import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import {
  CalendarRange, Loader2, Lock, RefreshCw,
} from "lucide-react";
import BlurredPreview from "../paywall/BlurredPreview";
import PaywallModal from "../paywall/PaywallModal";

/**
 * WeeklyNarrative — D6b (iter134).
 *
 * Reads /coach/weekly-narratives. Server-rendered deterministic
 * mad-lib recap of each ISO week. ZERO LLM.
 *
 * Free tier: last 4 narratives.
 * Elite: last 52 (+ regenerate action).
 */
export default function WeeklyNarrative() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(0);
  const [busyRegen, setBusyRegen] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/weekly-narratives");
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const items = data?.items || [];
  const elite = !!data?.elite;
  const hiddenCount = data?.hidden_count || 0;

  const regenerate = async (iy, iw) => {
    setBusyRegen(`${iy}:${iw}`);
    try {
      await api.post("/coach/weekly-narrative/regenerate",
                     { iso_year: iy, iso_week: iw });
      await load();
    } catch {
      /* silent */
    } finally {
      setBusyRegen(null);
    }
  };

  return (
    <section
      data-testid="weekly-narrative-section"
      className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-4"
    >
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalendarRange className="w-4 h-4 text-[var(--bt-accent)]" />
          <div>
            <p className="bt-label">Weekly Narrative</p>
            <h2 className="font-heading text-xl">Your Week, In Your Own Numbers</h2>
          </div>
        </div>
        <span
          data-testid="weekly-narrative-tier-badge"
          className={`px-2 py-0.5 text-xs font-mono uppercase border rounded-sm ${
            elite ? "text-[var(--bt-accent)] border-[var(--bt-accent)]"
                  : "text-[var(--bt-text-muted)] border-[var(--bt-border)]"
          }`}
        >
          {elite ? "Elite · Last 52" : "Free · Last 4"}
        </span>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--bt-text-muted)] py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="weekly-narrative-empty"
          className="text-sm text-[var(--bt-text-muted)] py-8 text-center border border-dashed border-[var(--bt-border)] rounded-sm"
        >
          No weekly recap yet — Coach writes one every Sunday for the
          just-ended week. Close a trade or log a thesis and the next
          Sunday will produce your first narrative.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="weekly-narrative-list">
          {items.map((it, idx) => (
            <WeeklyNarrativeCard
              key={`${it.iso_year}-${it.iso_week}`}
              narrative={it}
              expanded={idx === expandedIdx}
              onToggle={() => setExpandedIdx(idx === expandedIdx ? -1 : idx)}
              elite={elite}
              onRegenerate={() => regenerate(it.iso_year, it.iso_week)}
              regenerating={busyRegen === `${it.iso_year}:${it.iso_week}`}
            />
          ))}
        </ul>
      )}

      {!elite && hiddenCount > 0 && (
        <BlurredPreview
          hiddenCount={hiddenCount}
          unit="weekly recap"
          onUnlock={() => setPaywallOpen(true)}
        >
          <ul className="space-y-2">
            {Array.from({ length: Math.min(hiddenCount, 2) }).map((_, i) => (
              <li
                key={`wn-ph-${i}`}
                data-testid={`weekly-narrative-placeholder-${i}`}
                className="border border-[var(--bt-border)] rounded-sm h-16"
              />
            ))}
          </ul>
        </BlurredPreview>
      )}

      {!elite && items.length >= 4 && (
        <div
          data-testid="weekly-narrative-elite-upsell"
          className="border border-dashed border-[var(--bt-accent)] rounded-sm p-4 flex items-center gap-3 flex-wrap"
        >
          <Lock className="w-4 h-4 text-[var(--bt-accent)]" />
          <p className="text-sm text-[var(--bt-text-muted)] flex-1 min-w-[220px]">
            Free tier keeps your last 4 weekly narratives. Elite keeps
            a year of them, plus lets you regenerate any week on demand.
          </p>
          <Button
            data-testid="weekly-narrative-upgrade-btn"
            onClick={() => setPaywallOpen(true)}
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-9"
          >
            Upgrade to Elite
          </Button>
        </div>
      )}

      <PaywallModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        trigger="weekly_narrative"
      />
    </section>
  );
}

function formatWeekLabel(iy, iw) {
  return `${iy} · Week ${String(iw).padStart(2, "0")}`;
}

function formatMoney(amount, currency) {
  if (typeof amount !== "number") return "—";
  const sign = amount < 0 ? "-" : "+";
  const abs = Math.abs(amount);
  const rounded = abs >= 1000 ? Math.round(abs).toLocaleString()
                              : abs.toFixed(2);
  return `${sign}${currency || "USD"} ${rounded}`;
}

function WeeklyNarrativeCard({
  narrative, expanded, onToggle, elite, onRegenerate, regenerating,
}) {
  const { iso_year, iso_week, stats, paragraphs, currency,
          computed_at } = narrative;
  const pnlColor = stats.total_pnl > 0
    ? "text-[var(--bt-accent)]"
    : stats.total_pnl < 0
      ? "text-[var(--bt-loss)]"
      : "text-[var(--bt-text-muted)]";
  return (
    <li
      data-testid={`weekly-narrative-card-${iso_year}-${iso_week}`}
      className="border border-[var(--bt-border)] rounded-sm"
    >
      <button
        onClick={onToggle}
        data-testid={`weekly-narrative-toggle-${iso_year}-${iso_week}`}
        className="w-full flex items-center justify-between p-4 gap-3 flex-wrap text-left hover:bg-[var(--bt-bg)] transition-colors"
      >
        <div className="flex items-center gap-4 flex-wrap">
          <p className="font-heading text-lg">
            {formatWeekLabel(iso_year, iso_week)}
          </p>
          <div className="flex items-center gap-3 text-xs font-mono uppercase text-[var(--bt-text-muted)]">
            <span>{stats.trades_closed} trade{stats.trades_closed !== 1 ? "s" : ""}</span>
            <span>·</span>
            <span>{stats.wins}W / {stats.losses}L</span>
            {stats.contract_breaches > 0 && (
              <>
                <span>·</span>
                <span className="text-[var(--bt-loss)]">
                  {stats.contract_breaches} breach{stats.contract_breaches !== 1 ? "es" : ""}
                </span>
              </>
            )}
          </div>
        </div>
        <p
          className={`font-heading text-lg ${pnlColor}`}
          data-testid={`weekly-narrative-pnl-${iso_year}-${iso_week}`}
        >
          {formatMoney(stats.total_pnl, currency)}
        </p>
      </button>
      {expanded && (
        <div
          className="px-4 pb-4 space-y-3 border-t border-[var(--bt-border)] pt-3"
          data-testid={`weekly-narrative-body-${iso_year}-${iso_week}`}
        >
          {paragraphs.map((p, i) => (
            <p
              key={i}
              data-testid={`weekly-narrative-paragraph-${p.kind}-${iso_year}-${iso_week}`}
              className={`text-sm leading-relaxed ${
                p.kind === "milestone"
                  ? "text-[var(--bt-accent)]"
                  : p.kind === "contract_health"
                    ? "text-[var(--bt-text)]"
                    : "text-[var(--bt-text)]"
              }`}
            >
              {p.kind === "milestone" && "✦ "}
              {p.text}
            </p>
          ))}
          <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-dashed border-[var(--bt-border)]">
            <p
              data-testid={`weekly-narrative-source-${iso_year}-${iso_week}`}
              className="text-[10px] font-mono uppercase text-[var(--bt-text-muted)]"
            >
              server-generated · deterministic · no LLM
              {computed_at && (
                <>
                  {" · computed "}
                  {new Date(computed_at).toLocaleString()}
                </>
              )}
            </p>
            {elite && (
              <button
                onClick={onRegenerate}
                disabled={regenerating}
                data-testid={`weekly-narrative-regenerate-${iso_year}-${iso_week}`}
                className="text-xs font-mono uppercase text-[var(--bt-text-muted)] hover:text-[var(--bt-accent)] flex items-center gap-1"
              >
                <RefreshCw className={`w-3 h-3 ${regenerating ? "animate-spin" : ""}`} />
                {regenerating ? "Recomputing…" : "Regenerate"}
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
